import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Check, X, ExternalLink } from 'lucide-react';

const PaymentsSection = ({
  payments,
  pendingPayments,
  totalRevenue,
  handleConfirmPayment,
  handleRejectPayment
}) => {
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Payments</h1>
          <p className="text-gray-500">Track revenue and manage payments</p>
        </div>
        <button 
          onClick={() => navigate('/admin/payments')}
          className="bg-purple-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <ExternalLink className="w-4 h-4" />
          Full Payment Dashboard
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
        <div className="bg-white rounded-xl border p-6">
          <p className="text-sm text-gray-500">Total Revenue</p>
          <p className="text-3xl font-bold text-green-600">£{totalRevenue.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl border p-6">
          <p className="text-sm text-gray-500">Completed Payments</p>
          <p className="text-3xl font-bold">{payments.filter(p => p.status === 'completed').length}</p>
        </div>
        <div className="bg-white rounded-xl border p-6">
          <p className="text-sm text-gray-500">Pending Payments</p>
          <p className="text-3xl font-bold text-orange-600">{pendingPayments.length}</p>
        </div>
      </div>

      {/* Recent payments */}
      <div className="bg-white rounded-xl border">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="font-semibold">Recent Payments</h2>
          <span className="text-sm text-gray-500">{payments.length} total</span>
        </div>
        <div className="divide-y">
          {payments.slice(0, 15).map((payment) => (
            <div key={payment.id || payment.session_id} className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  payment.status === 'completed' ? 'bg-green-100' :
                  payment.status === 'pending' || payment.status === 'initiated' ? 'bg-yellow-100' :
                  'bg-red-100'
                }`}>
                  <CreditCard className={`w-5 h-5 ${
                    payment.status === 'completed' ? 'text-green-600' :
                    payment.status === 'pending' || payment.status === 'initiated' ? 'text-yellow-600' :
                    'text-red-600'
                  }`} />
                </div>
                <div>
                  <p className="font-medium">{payment.user_name || payment.user_email}</p>
                  <p className="text-sm text-gray-500">{payment.package_name || payment.payment_type}</p>
                  <p className="text-xs text-gray-400">
                    {payment.created_at ? new Date(payment.created_at).toLocaleDateString() : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="font-semibold">£{payment.amount}</p>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    payment.status === 'completed' ? 'bg-green-100 text-green-700' :
                    payment.status === 'pending' || payment.status === 'initiated' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {payment.status}
                  </span>
                </div>
                {/* Action buttons for pending/initiated payments */}
                {(payment.status === 'pending' || payment.status === 'initiated') && payment.session_id && (
                  <div className="flex gap-2 ml-2">
                    <button
                      onClick={() => handleConfirmPayment(payment.session_id, payment.user_email)}
                      className="p-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg"
                      title="Confirm Payment"
                      data-testid={`confirm-payment-${payment.session_id}`}
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleRejectPayment(payment.session_id, payment.user_email)}
                      className="p-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg"
                      title="Reject Payment"
                      data-testid={`reject-payment-${payment.session_id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
          {payments.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              No payments yet
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentsSection;
